import { validate as validateUuid, version as uuidVersion } from 'uuid'

/**
 * Barcode/QR scanner support for the check-in kiosk.
 *
 * The scanners used at check-in are USB "keyboard wedge" devices: they type the
 * decoded payload as a burst of keystrokes and (usually) an Enter or Tab
 * suffix. There is no device API to bind to — from the browser's side a scan is
 * indistinguishable from a very fast typist, which is why detection lives here
 * rather than in an event source.
 *
 * A scan payload is always the `kiosk-attendees` Firestore **document ID**: the
 * badge QR encodes `useConfirmationAsId ? confirmationCode : attendee.id`
 * (`BadgePreview`), and the API writes the doc under the matching key. So a
 * recognized payload resolves with a single document read — it never needs the
 * attendee search.
 *
 * Two paths use these helpers:
 *
 * 1. Nothing focused — a document-level `keydown` listener buffers the burst
 *    (`EventKiosk`).
 * 2. The search box focused — the payload lands in the input as normal, and the
 *    input's value is tested here before it is treated as a search term
 *    (`Staff`, and the attendee-facing search in `EventKiosk`).
 *
 * Path 2 is why detection is content-based rather than timing- or prefix-based:
 * it needs no scanner configuration and works identically for a scan, a paste,
 * or a code typed by hand.
 */

/**
 * Characters a scanner can emit as part of a payload. Confirmation codes are
 * alphanumeric; EventsAir joins its two segments with a comma, and UUIDs use
 * hyphens.
 */
const SCAN_PAYLOAD_CHARACTERS = /^[a-zA-Z0-9,-]+$/

/**
 * Shortest opaque confirmation code we will treat as a scan on its own.
 * Stova reference numbers are 8 numeric digits; Cvent confirmation numbers are
 * 13 alphanumeric characters. Anything shorter is far more likely to be someone
 * typing a name.
 */
const MIN_OPAQUE_CODE_LENGTH = 8

/**
 * True if `value` is a v4 UUID — the attendee ID, and the kiosk document ID for
 * events that do not use confirmation codes as IDs.
 * @param {string} value
 * @returns {boolean}
 */
export const isAttendeeUuid = (value) => validateUuid(value) && uuidVersion(value) === 4

/**
 * True if `key` is a single printable character.
 *
 * `KeyboardEvent.key` for a non-printable key is a *word* — `Escape`, `Enter`,
 * `ArrowDown`, `Backspace`, `Control`. Buffering those verbatim (as the scan
 * capture used to) produced strings that passed a loose alphanumeric check and
 * were then looked up as attendee IDs, so any stray keypress outside an input
 * raised "Error Getting Attendee Data".
 * @param {string} key
 * @returns {boolean}
 */
export const isPrintableKey = (key) => typeof key === 'string' && [...key].length === 1

/**
 * True if `key` is a suffix a scanner may send to mark the end of a payload.
 * Used to resolve a scan immediately instead of waiting out the idle timeout.
 * @param {string} key
 * @returns {boolean}
 */
export const isScanTerminator = (key) => key === 'Enter' || key === 'Tab'

/**
 * True if `target` is somewhere the user is typing, and therefore somewhere the
 * document-level scan capture must keep its hands off.
 * @param {EventTarget} target
 * @returns {boolean}
 */
export const isEditableTarget = (target) => {
  if (!target || !target.tagName) return false

  const tagName = target.tagName.toUpperCase()

  return tagName === 'INPUT' || tagName === 'TEXTAREA' || target.isContentEditable === true
}

/**
 * True if `value` looks like a scanned check-in payload rather than a search
 * term someone typed.
 *
 * Recognized shapes, all of which are valid kiosk document IDs:
 * - a v4 UUID (attendee ID)
 * - `<internalNumber>,<v4 UUID>` (EventsAir confirmation code, EXEC-1532)
 * - an opaque code: at least `MIN_OPAQUE_CODE_LENGTH` characters, no
 *   whitespace, and containing at least one digit (Cvent, Stova)
 *
 * The digit requirement is what keeps real names out: no confirmation code
 * scheme we sync is pure letters, and no last name carries a digit. A false
 * positive costs one wasted document read and falls through to the normal
 * search, so the check is deliberately tuned to accept rather than to be
 * exhaustive — organizer-authored custom QR codes can hold arbitrary text and
 * are not check-in payloads at all.
 * @param {string} value
 * @returns {boolean}
 */
export const looksLikeScanPayload = (value) => {
  if (typeof value !== 'string') return false

  const candidate = value.trim()

  if (!candidate || !SCAN_PAYLOAD_CHARACTERS.test(candidate)) return false

  // Attendee ID
  if (isAttendeeUuid(candidate)) return true

  // EventsAir `<internalNumber>,<uuid>`
  const separatorIndex = candidate.indexOf(',')
  if (separatorIndex > 0 && isAttendeeUuid(candidate.slice(separatorIndex + 1))) {
    return true
  }

  // Opaque confirmation code
  return candidate.length >= MIN_OPAQUE_CODE_LENGTH && /\d/.test(candidate)
}
